import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Automation, ChainOperator, TriggersSetupArgs, TriggerType } from '../../../builder.types';
import { Store } from '@ngxs/store';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { Observable, tap } from 'rxjs';
import { FieldDefsState } from 'app/state/fields/state';
import { FieldDef } from 'app/core/models';
import { Dictionary } from 'app/shared/models';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { FormBase } from '../builder-form.types';

@Component({
  selector: 'app-automation-builder-triggers-setup-form',
  templateUrl: './triggers-setup.component.html',
  styleUrls: ['./triggers-setup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TriggersSetupFormComponent extends FormBase<TriggersSetupArgs> implements OnInit {
  @Input() formData: any;
  @Input() set id(value: number) {
    if (value) {
      const trigger = this.automation.triggers.find(({ id }) => id === value);
      this.form.setValue({
        id: value,
        objectFieldDef:
          trigger.type === TriggerType.ObjectCreated
            ? null
            : trigger.attr
            ? this.fieldDefs.find(
                ({ name, related_to_type }) =>
                  name === trigger.attr && related_to_type === this.automation.target_model,
              )
            : null,
        trigger: trigger.type,
        objectValueFilters: [
          {
            operator: 'eq',
            value: trigger.value,
            chainOperator: 'none',
          },
        ],
      });

      if (trigger.type === TriggerType.AttributeChange && trigger.attr) {
        let value = +trigger.value;
        if (isNaN(value)) {
          value = trigger.value;
        }
        this.model = { [trigger.attr]: value };
      }

      setTimeout(() => {
        this.form.get('trigger').updateValueAndValidity();
        this.form.get('objectFieldDef').updateValueAndValidity({ emitEvent: false });
      }, 0);
    }
  }

  objects: CustomObject[];
  fieldDefs: FieldDef[];
  TriggerType = TriggerType;
  form = new FormGroup({
    id: new FormControl(null, [Validators.required]),
    trigger: new FormControl(null, [Validators.required]),
    objectFieldDef: new FormControl(null, [Validators.required]),
    objectValueFilters: new FormArray([
      new FormGroup({
        operator: new FormControl('eq', [Validators.required]),
        value: new FormControl('', [Validators.required]),
        chainOperator: new FormControl('none'),
      }),
    ]),
  });
  triggers = [
    { label: 'New Object', value: TriggerType.ObjectCreated },
    { label: 'Data Change', value: TriggerType.AttributeChange },
  ];
  trigger$: Observable<string>;
  model: any = {};

  get objectValueFilters(): FormArray {
    return this.form.get('objectValueFilters') as FormArray;
  }

  constructor(private readonly store: Store) {
    super();

    this.objects = this.store.selectSnapshot(ObjectsManagerState.get);
    this.fieldDefs = this.store.selectSnapshot(FieldDefsState.getFieldDefs);
  }

  ngOnInit(): void {
    this.form
      .get('objectFieldDef')
      .valueChanges.pipe(untilComponentDestroyed(this))
      .subscribe((x) => {
        this.objectValueFilters.clear();

        if (this.form.get('trigger').value === TriggerType.AttributeChange) {
          this.objectValueFilters.push(
            new FormGroup({
              operator: new FormControl('eq', [Validators.required]),
              value: new FormControl('', [Validators.required]),
              chainOperator: new FormControl('none'),
            }),
          );
        }
      });

    this.trigger$ = this.form.get('trigger').valueChanges.pipe(
      untilComponentDestroyed(this),
      tap((value: TriggerType) => {
        if (value === TriggerType.ObjectCreated) {
          this.form.get('objectFieldDef').setValidators(null);
          this.form.get('objectFieldDef').updateValueAndValidity();
        } else if (value === TriggerType.AttributeChange) {
          this.form.get('objectFieldDef').setValidators([Validators.required]);
          this.form.get('objectFieldDef').updateValueAndValidity();
        }
      }),
    );
  }

  onFieldUpdate(model: Dictionary<any>, objectValueFilter: FormGroup) {
    objectValueFilter.get('value').setValue(model[this.form.get('objectFieldDef').value.name]);
  }

  changeChainOperator(chainOperator: ChainOperator, index: number, isLast: boolean): void {
    this.objectValueFilters.at(index).get('chainOperator').setValue(chainOperator);

    if (isLast) {
      this.objectValueFilters.push(
        new FormGroup({
          operator: new FormControl('', [Validators.required]),
          value: new FormControl('', [Validators.required]),
          chainOperator: new FormControl('none'),
        }),
      );
    } else {
      if (chainOperator === 'none') {
        while (this.objectValueFilters.controls.length > index + 1) {
          this.objectValueFilters.removeAt(index + 1);
        }
      }
    }
  }

  save(): void {
    const formValue = this.form.getRawValue();
    let model: TriggersSetupArgs;

    switch (formValue.trigger) {
      case TriggerType.ObjectCreated:
        model = {
          triggers: [
            {
              id: formValue.id,
              attr: 'object_type',
              type: TriggerType.ObjectCreated,
              value: this.automation.target_model,
            },
          ],
        };
        break;
      case TriggerType.AttributeChange:
        model = {
          triggers: [
            {
              id: formValue.id,
              attr: formValue.objectFieldDef.name,
              type: TriggerType.AttributeChange,
              value: formValue.objectValueFilters[0].value,
            },
          ],
        };
        break;
    }

    this.ok.emit(model);
  }

  removeFilter(index: number): void {
    this.objectValueFilters.removeAt(index);
  }

  addFilter(): void {
    this.objectValueFilters.push(
      new FormGroup({
        operator: new FormControl('', [Validators.required]),
        value: new FormControl('', [Validators.required]),
        chainOperator: new FormControl('none'),
      }),
    );
  }
}
